import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Plus, Calendar, CheckCircle2, Circle, Target } from 'lucide-react'
import { useState } from 'react'

import { MilestoneForm } from '@/components/pm/milestone/milestone-form'
import { useConfirmDialog, useProject } from '@/hooks'
import {
  useMilestones,
  useDeleteMilestone,
  type MilestoneStatus,
  type MilestoneListParams,
} from '@/hooks'
import { cn } from '@/lib/utils'

/**
 * 项目里程碑页面
 */
export const Route = createFileRoute('/_authenticated/pm/projects/$projectId/milestones')({
  component: MilestonesPage,
})

const STATUS_LABELS: Record<MilestoneStatus, string> = {
  open: '进行中',
  closed: '已完成',
}

function MilestonesPage() {
  const params = Route.useParams()
  const projectId = params.projectId
  const navigate = useNavigate()
  const [filters, setFilters] = useState<MilestoneListParams>({
    sortBy: 'dueDate',
    sortOrder: 'asc',
  })
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const { data: project } = useProject(projectId)
  const { data: milestones, isLoading, error } = useMilestones(projectId, filters)
  const deleteMutation = useDeleteMilestone()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const handleStatusFilter = (status: MilestoneStatus | undefined) => {
    setFilters((prev) => ({ ...prev, status }))
  }

  const handleEdit = (id: string) => {
    setEditingId(id)
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    const confirmed = await confirmWithPreset(
      'delete',
      '确定要删除这个里程碑吗？'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await deleteMutation.mutateAsync(id)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingId(null)
  }

  // 计算统计数据
  const stats = milestones ? {
    total: milestones.length,
    open: milestones.filter((m) => m.status === 'open').length,
    closed: milestones.filter((m) => m.status === 'closed').length,
    overdue: milestones.filter((m) =>
      m.status === 'open' && m.dueDate && new Date(m.dueDate) < new Date()
    ).length,
  } : null

  if (error) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-destructive">加载失败</div>
        <Link
          to="/pm/projects/$projectId"
          params={{ projectId }}
          className="text-sm text-primary hover:underline"
        >
          返回项目详情
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 头部 */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => void navigate({ to: '/pm/projects/$projectId', params: { projectId } })}
          className="rounded-md p-2 hover:bg-muted"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <p className="text-sm text-muted-foreground">{project?.code}</p>
          <h1 className="text-2xl font-bold tracking-tight">里程碑</h1>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          新建里程碑
        </button>
      </div>

      {/* 统计卡片 */}
      {stats && (
        <div className="grid gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Target className="h-4 w-4" />
              总计
            </div>
            <div className="mt-1 text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Circle className="h-4 w-4" />
              进行中
            </div>
            <div className="mt-1 text-2xl font-bold text-blue-600">{stats.open}</div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4" />
              已完成
            </div>
            <div className="mt-1 text-2xl font-bold text-green-600">{stats.closed}</div>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              已逾期
            </div>
            <div className="mt-1 text-2xl font-bold text-red-600">{stats.overdue}</div>
          </div>
        </div>
      )}

      {/* 筛选 */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleStatusFilter(undefined)}
          className={cn(
            'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            !filters.status ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
          )}
        >
          全部
        </button>
        <button
          onClick={() => handleStatusFilter('open')}
          className={cn(
            'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            filters.status === 'open' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
          )}
        >
          进行中
        </button>
        <button
          onClick={() => handleStatusFilter('closed')}
          className={cn(
            'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            filters.status === 'closed' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
          )}
        >
          已完成
        </button>
      </div>

      {/* 里程碑列表 */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : !milestones || milestones.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <Target className="h-12 w-12 text-muted-foreground/50" />
          <div className="text-center">
            <p className="text-lg font-medium">暂无里程碑</p>
            <p className="text-sm text-muted-foreground">点击上方按钮创建第一个里程碑</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {milestones.map((milestone) => {
            const isOverdue = milestone.status === 'open' &&
              milestone.dueDate &&
              new Date(milestone.dueDate) < new Date()

            return (
              <div
                key={milestone.id}
                className="rounded-lg border bg-card p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {milestone.status === 'closed' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <Circle className="h-5 w-5 text-blue-600" />
                      )}
                      <h3 className="font-semibold">{milestone.name}</h3>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          milestone.status === 'closed'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                        )}
                      >
                        {STATUS_LABELS[milestone.status]}
                      </span>
                      {isOverdue && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                          已逾期
                        </span>
                      )}
                    </div>
                    {milestone.description && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        {milestone.description}
                      </p>
                    )}
                    <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
                      {milestone.dueDate && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          截止: {new Date(milestone.dueDate).toLocaleDateString('zh-CN')}
                        </div>
                      )}
                      <div>
                        任务: {milestone.completedIssues}/{milestone.totalIssues}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(milestone.id)}
                      className="text-sm text-primary hover:underline"
                    >
                      编辑
                    </button>
                    <button
                      onClick={() => void handleDelete(milestone.id)}
                      className="text-sm text-destructive hover:underline"
                      disabled={deleteMutation.isPending}
                    >
                      删除
                    </button>
                  </div>
                </div>

                {/* 进度条 */}
                <div className="mt-4">
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span>进度</span>
                    <span>{milestone.progress}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted">
                    <div
                      className={cn(
                        'h-2 rounded-full transition-all',
                        milestone.progress === 100 ? 'bg-green-500' : 'bg-primary'
                      )}
                      style={{ width: `${milestone.progress}%` }}
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* 里程碑表单弹窗 */}
      {showForm && (
        <MilestoneForm
          projectId={projectId}
          milestoneId={editingId}
          onClose={handleFormClose}
        />
      )}
      <ConfirmDialog />
    </div>
  )
}
